<template>
  <section class="emotional-monitoring">
    <div class="emotional-monitoring__header">
      <h2 class="emotional-monitoring__title">
        Мониторинг эмоциональной динамики
      </h2>

      <div class="emotional-monitoring__filters">
        <a-select
            v-model:value="selectedGroup"
            :options="groupOptions"
            :disabled="!hasSubscription"
            class="emotional-monitoring__select"
        />

        <div class="emotional-monitoring__period-control">
          <a-select
              v-model:value="selectedPeriod"
              :options="displayPeriodOptions"
              :disabled="!hasSubscription"
              class="emotional-monitoring__period"
              @change="handlePeriodChange"
          >
            <template #suffixIcon>
              <CalendarOutlined
                  class="emotional-monitoring__calendar-icon"
                  @click.stop="rangePickerOpen = true"
              />
            </template>
          </a-select>

          <a-range-picker
              v-model:value="customRange"
              v-model:open="rangePickerOpen"
              class="emotional-monitoring__hidden-range-picker"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              @change="handleRangeChange"
          />
        </div>
      </div>
    </div>

    <div class="emotional-monitoring__grid">
      <SmallCardWrapper
          name="Энергия компании"
          :helpText="'Средний уровень энергии выбранного подразделения в заданный период, в процентах:<br>Очень высокий - 81%-100%<br>Высокий - 61% - 80%<br>Средний - 41% - 60%<br>Низкий - 21% - 40%<br>Очень низкий - 0 - 20%'"
          :subtitle="energy.passedCount ? formatPassedCount(energy.passedCount) : ''"
          :loading="dataIsLoading"
          :locked="!hasSubscription"
          :showLockedContent="true"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="'Отслеживайте уровень энергии в компании за разный период времени и находите проблемные места.'" />
        </div>

        <CompanyEnergyCard
            :total="energy.total"
            :levels="energy.levels"
            :chartLabels="energy.chartLabels"
            :chartValues="energy.chartValues"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Топ эмоций"
          :helpText="'В виджете показаны пять наиболее часто встречающихся эмоций сотрудников за данный период. Справа - процент сотрудников, выбравших данную эмоцию. Стрелка показывает рост/снижение процента сотрудников, выбравших данную эмоцию по сравнению с предыдущим периодом.'"
          :subtitle="topEmotions.passedCount ? formatPassedCount(topEmotions.passedCount) : ''"
          :loading="dataIsLoading"
          :locked="!hasSubscription"
          :showLockedContent="true"
      >
        <div v-if="!hasSubscription" class="subsription-locked">
          <SubscriptionRequiredWarning
              @showOrder="showOrderForm"
              :customText="'Следите за настроением команды, изменениями эмоций.'" />
        </div>

        <TopEmotionsCard
            :warning="topEmotions.warning"
            :items="topEmotions.items"
            :class="{ locked: !hasSubscription }"
        />
      </SmallCardWrapper>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import dayjs from 'dayjs'
import { CalendarOutlined } from '@ant-design/icons-vue'

import SmallCardWrapper from '@/components/Widgets/WidgetCard.vue'
import TopEmotionsCard from '@/components/Analytics/TopEmotionCards.vue'
import CompanyEnergyCard from '@/components/Analytics/CompanyEnergyCard.vue'
import AssertionsDynamicsCard from '@/components/Analytics/AssertionsDynamicsCard.vue'
import SubscriptionRequiredWarning from "@/components/OtherElements/SubscriptionRequiredWarning.vue"
import { formatMonitoringDateForPicker, getMonitoringDateSetting, setMonitoringDateSetting } from "@/utils/monitoringDateStorage"
import { getWordFormBasedOnTheCount } from '@/views/Report/utils'

export default {
  name: 'EmotionalDynamicsMonitoring',

  props: {
    hasSubscription: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    SubscriptionRequiredWarning,
    AssertionsDynamicsCard,
    CompanyEnergyCard,
    TopEmotionsCard,
    SmallCardWrapper,
    CalendarOutlined,
  },

  setup(props, context) {
    const store = useStore();
    const groups = computed(() => store.state.group.groups);
    const dateStorageKey = 'monitoring:emotional-dynamics:date'
    const defaultDateSetting = {
      selectedPeriod: 'week',
      customRange: null,
    }
    const savedDateSetting = getMonitoringDateSetting(dateStorageKey, defaultDateSetting)
    const formatRangeForPicker = (range) => Array.isArray(range) ? range.map(formatMonitoringDateForPicker) : range

    const customRangeIsValid = (range) => {
      return Array.isArray(range) && range.length === 2 && range[0] && range[1]
    }
    const savedCustomRange = formatRangeForPicker(savedDateSetting?.customRange)
    const savedCustomRangeIsValid = customRangeIsValid(savedCustomRange)
    const hasSavedCustomDate = savedDateSetting?.selectedPeriod === 'custom' && savedCustomRangeIsValid
    const hasSavedPeriodDate = Boolean(
        savedDateSetting?.selectedPeriod &&
        savedDateSetting.selectedPeriod !== defaultDateSetting.selectedPeriod &&
        savedDateSetting.selectedPeriod !== 'custom'
    )
    const shouldFetchSavedDate = hasSavedCustomDate || hasSavedPeriodDate
    const initialSelectedPeriod =
        savedDateSetting?.selectedPeriod === 'custom' && !savedCustomRangeIsValid
            ? defaultDateSetting.selectedPeriod
            : savedDateSetting?.selectedPeriod || defaultDateSetting.selectedPeriod

    const selectedGroup = ref('all')
    const selectedPeriod = ref(initialSelectedPeriod)
    const customRange = ref(savedCustomRangeIsValid ? savedCustomRange : defaultDateSetting.customRange)
    const rangePickerOpen = ref(false)
    const dataIsLoading = ref(false)

    const hasSubscriptionAccess = computed(() => props.hasSubscription);
    const shouldUseMonitoringIndexData = computed(() => {
      return selectedGroup.value === 'all' &&
          selectedPeriod.value === defaultDateSetting.selectedPeriod &&
          !customRange.value
    })

    const groupOptions = computed(() => {
      return [
        {
          label: 'Все сотрудники',
          value: 'all',
        },
        ...groups.value.map((group) => ({
          label: group.name,
          value: group.id,
        })),
      ]
    })

    const basePeriodOptions = [
      {
        label: 'Неделя',
        value: 'week',
      },
      {
        label: 'Месяц',
        value: 'month',
      },
      {
        label: 'Квартал',
        value: 'quarter',
      },
      {
        label: 'Год',
        value: 'year',
      },
    ]

    const emotionalDynamicsData = computed(() => {
      return store.state.analytics.monitoringData?.emotionalDynamics ?? null
    })

    const getPeriodRange = (period) => {
      const now = dayjs()

      const map = {
        week: {
          from: now.subtract(7, 'day'),
          to: now,
        },
        month: {
          from: now.subtract(1, 'month'),
          to: now,
        },
        quarter: {
          from: now.subtract(3, 'month'),
          to: now,
        },
        year: {
          from: now.subtract(1, 'year'),
          to: now,
        },
      }

      const range = map[period] || map.week

      return {
        date_from: range.from.format('YYYY.MM.DD'),
        date_to: range.to.format('YYYY.MM.DD'),
      }
    }

    const customRangeLabel = computed(() => {
      if (!customRange.value || customRange.value.length !== 2) {
        return 'Выбрать период'
      }


      const [from, to] = customRange.value

      return `${dayjs(from).format('DD.MM.YYYY')} - ${dayjs(to).format('DD.MM.YYYY')}`
    })

    const displayPeriodOptions = computed(() => {
      const options = [...basePeriodOptions]

      if (selectedPeriod.value === 'custom') {
        options.push({
          label: customRangeLabel.value,
          value: 'custom',
        })
      }

      return options
    })

    const periodInterval = computed(() => {
      if (
          selectedPeriod.value === 'custom' &&
          Array.isArray(customRange.value) &&
          customRange.value.length === 2
      ) {
        const [from, to] = customRange.value

        return [
          dayjs(from).format('YYYY.MM.DD'),
          dayjs(to).format('YYYY.MM.DD'),
        ].join('-')
      }

      const range = getPeriodRange(selectedPeriod.value)

      return [range.date_from, range.date_to].join('-')
    })

    const ENERGY_LEVELS_META = [
      {
        key: 'very_high',
        sourceKey: 'very_high',
        label: 'Очень высокий',
        color: '#21AC0C',
      },
      {
        key: 'high',
        sourceKey: 'high',
        label: 'Высокий',
        color: '#99DA6E',
      },
      {
        key: 'medium',
        sourceKey: 'medium',
        label: 'Средний',
        color: '#DDD71D',
      },
      {
        key: 'low',
        sourceKey: 'low',
        label: 'Низкий',
        color: '#FF9F43',
      },
      {
        key: 'very_low',
        sourceKey: 'very_low',
        label: 'Очень низкий',
        color: '#F55565',
      },
    ]

    const EMPTY_TOP_EMOTIONS_WARNING = {
      text: 'В компании растет напряжение.',
      recommendations: [
        'Собрать обратную связь на встречах.',
        'Провести встречи 1-на-1.',
      ],
    }

    const DEFAULT_ENERGY = {
      total: 76,
      levels: [
        {
          key: 'very_high',
          label: 'Очень высокий',
          value: 3,
          color: '#21AC0C',
        },
        {
          key: 'high',
          label: 'Высокий',
          value: 10,
          color: '#99DA6E',
        },
        {
          key: 'medium',
          label: 'Средний',
          value: 39,
          color: '#DDE00F',
        },
        {
          key: 'low',
          label: 'Низкий',
          value: 12,
          color: '#FF9F40',
        },
        {
          key: 'very_low',
          label: 'Очень низкий',
          value: 5,
          color: '#F55565',
        },
      ],
      chartLabels: ['07.03', '14.03', '21.03', '28.03', '04.04', '11.04'],
      chartValues: [50, 35, 31, 32, 46, 85],
    }

    const DEFAULT_TOP_EMOTIONS = {
      warning: null,
      items: [
        {
          title: 'Раздражение',
          trend: 'up',
          score: '16',
        },
        {
          title: 'Спокойствие',
          trend: 'up',
          score: '9',
        },
        {
          title: 'Усталость',
          trend: 'up',
          score: '16',
        },
        {
          title: 'Радость',
          trend: 'down',
          score: '3',
        },
        {
          title: 'Интерес',
          trend: 'up',
          score: '65',
        },
      ],
    }

    const energy = ref({
      total: 0,
      levels: [],
      chartLabels: [],
      chartValues: [],
      passedCount: null,
    })

    const topEmotions = ref({
      warning: null,
      items: [],
      passedCount: null,
    })

    const getSafeNumber = (value) => {
      const numericValue = Number(value)
      return Number.isFinite(numericValue) ? numericValue : 0
    }

    const formatPassedCount = (count) => {
      return `(${count} ${getWordFormBasedOnTheCount(count, 'человек', 'человека', 'человек')})`
    }

    const buildEnergyChartData = (chart = {}) => {
      const labels = Array.isArray(chart?.labels) ? chart.labels : []
      const values = Array.isArray(chart?.value) ? chart.value : []
      const itemsCount = Math.min(labels.length, values.length)

      return {
        chartLabels: labels.slice(0, itemsCount),
        chartValues: values.slice(0, itemsCount).map((value) => getSafeNumber(value)),
      }
    }

    const buildEnergyFromBackend = (data) => {
      const companyEnergy = data?.company_energy || {}
      const total = getSafeNumber(companyEnergy.avg)

      return {
        total,
        passedCount: companyEnergy.passed_count,
        levels: ENERGY_LEVELS_META.map((item) => ({
          key: item.key,
          label: item.label,
          value: getSafeNumber(companyEnergy[item.sourceKey]),
          color: item.color,
        })),
        ...buildEnergyChartData(companyEnergy.chart),
      }
    }

    const buildTopEmotionsFromBackend = (data) => {
      const topEmotions = data?.top_emotions || {}
      const backendItems = Array.isArray(topEmotions.emotions)
          ? topEmotions.emotions
          : []

      return {
        warning: null,
        passedCount: topEmotions.passed_count,
        items: backendItems.length
            ? backendItems.slice(0, 5).map((item) => ({
              title: item?.emotion_name || '',
              trend: item?.dynamics || 'neutral',
              score: String(getSafeNumber(item?.percent)),
            }))
            : [],
      }
    }

    const fillCardsData = (backendData) => {
      if (!hasSubscriptionAccess.value) {
        energy.value = {
          ...DEFAULT_ENERGY,
          levels: DEFAULT_ENERGY.levels.map((item) => ({ ...item })),
          chartLabels: [...DEFAULT_ENERGY.chartLabels],
          chartValues: [...DEFAULT_ENERGY.chartValues],
          passedCount: null,
        }

        topEmotions.value = {
          warning: null,
          passedCount: null,
          items: DEFAULT_TOP_EMOTIONS.items.map((item) => ({ ...item })),
        }

        return
      }

      energy.value = buildEnergyFromBackend(backendData)
      topEmotions.value = buildTopEmotionsFromBackend(backendData)
    }

    const fetchData = () => {
      if (!hasSubscriptionAccess.value) {
        fillCardsData(null)
        return
      }

      dataIsLoading.value = true

      store.dispatch('analytics/getEmotionalDynamics', {
        group_id: selectedGroup.value === 'all' ? null : selectedGroup.value,
        date: periodInterval.value,
      }).then(() => {
        fillCardsData(emotionalDynamicsData.value)
      }).finally(() => {
        dataIsLoading.value = false
      })
    }

    const handlePeriodChange = (value) => {
      if (value !== 'custom') {
        customRange.value = null
      }
    }

    const handleRangeChange = (dates) => {
      if (Array.isArray(dates) && dates.length === 2 && dates[0] && dates[1]) {
        selectedPeriod.value = 'custom'
      }
    }

    const showOrderForm = () => {
      context.emit('showOrderForm');
    }

    watch(
        () => [emotionalDynamicsData.value, hasSubscriptionAccess.value],
        () => {
          if (!shouldUseMonitoringIndexData.value) {
            return
          }

          fillCardsData(emotionalDynamicsData.value)
        },
        { immediate: true }
    )

    watch(
        hasSubscriptionAccess,
        (hasAccess, hadAccess) => {
          if (hasAccess && !hadAccess) {
            fetchData()
          }
        }
    )

    watch(
        () => [selectedGroup.value, selectedPeriod.value, periodInterval.value],
        () => {
          fetchData()
        },
        { immediate: shouldFetchSavedDate }
    );

    watch(
        () => [selectedPeriod.value, customRange.value],
        () => {
          if (selectedPeriod.value !== 'custom' || customRangeIsValid(customRange.value)) {
            setMonitoringDateSetting(dateStorageKey, {
              selectedPeriod: selectedPeriod.value,
              customRange: customRange.value,
            })
          }
        }
    )

    return {
      selectedGroup,
      selectedPeriod,
      customRange,
      rangePickerOpen,
      groupOptions,
      displayPeriodOptions,
      energy,
      topEmotions,
      dataIsLoading,
      formatPassedCount,
      handlePeriodChange,
      handleRangeChange,
      showOrderForm
    }
  },
}
</script>

<style scoped lang="scss">
.emotional-monitoring {
  width: 100%;
  text-align: start;
}

.emotional-monitoring__header {
  margin-bottom: 20px;
}

.emotional-monitoring__title {
  margin: 0 0 20px;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);

  @media (max-width: $mobile-breakpoint) {
    padding: 0 20px;
  }
}

.emotional-monitoring__filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.emotional-monitoring__select {
  width: 350px;
}

.emotional-monitoring__period-control {
  position: relative;
  width: 200px;
}

.emotional-monitoring__period {
  width: 100%;
}

.emotional-monitoring__calendar-icon {
  font-size: 18px;
  color: rgba(0, 0, 0, 0.35);
  cursor: pointer;
}

.emotional-monitoring__hidden-range-picker {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.emotional-monitoring__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.emotional-monitoring__grid :deep(.ant-card-body) {
  padding: 0;
}

.emotional-monitoring__grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

@media (max-width: $tablet-breakpoint) {
  .emotional-monitoring__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .emotional-monitoring__filters {
    padding: 0 20px;
  }

  .emotional-monitoring__select,
  .emotional-monitoring__period-control,
  .emotional-monitoring__period {
    width: 100%;
  }
}

.locked {
  opacity: 0.5;
}

.subsription-locked {
  margin-bottom: 16px;
}
</style>
